<template>
  <div v-if="cardStatus.show"
       class="scene"
       :class="{ animate: cardStatus.animate }"
  >
    <b-card class="shadow-none hint-inner" :class="{ flipped: cardStatus.flip, 'no-transition': isIE }">
      <div class="front-side side d-flex flex-column p-4" :class="classes">
        <div class="mb-2 title-wrapper">
          <p class="mb-0">{{ title }}</p>
          <b-button
            v-if="!hideCloseButton"
            class="icon mt-n1 reset-button"
            variant="transparent"
            data-cy="hide-close-button"
            @click="hideHintWithAnimation"
          >
            <span class="sr-only">{{ $t('global.close') }}</span>
            <sdui-icon class="icon-24 icon-close-o" />
          </b-button>
        </div>
        <div class="description mb-2">
          <slot name="description">{{ description }}</slot>
        </div>

        <slot v-if="!hideAction" name="action">
          <component
            :is="isNavigationalAction ? 'SLink' : 'b-button'"
            :appearance="isNavigationalAction ? 'b-button' : undefined"
            :href="isNavigationalAction ? '#' : undefined"
            size="sm"
            pill
            :class="buttonClasses"
            class="small hint-button"
            :data-cy="buttonCyData"
            @click="handleAction"
          >
            {{ buttonText }}
          </component>
        </slot>
      </div>
      <slot name="back-side">
        <div class="back-side side">
          <div class="text-center d-block w-100">
            <p class="mb-2 text-muted nano">{{ passiveText || $t('hints.passive_text') }}</p>
            <b-button
              v-if="showUndoButton"
              ref="undoButton"
              size="sm"
              class="small hint-button"
              :tabindex="!cardStatus.flip ? -1 : null"
              @click="cancelHide"
            >
              {{ $t('hints.passive_button') }}
            </b-button>
            <p v-if="passiveSecondaryText"
               class="mt-2 text-muted nano passive-text"
               v-html="$sanitize(passiveSecondaryText)"
            />
          </div>
        </div>
      </slot>
    </b-card>
  </div>
</template>

<script>
export default {
  name: 'SduiCard',
  props: {
    cardState: {
      // We have this prop to close item from parent and hide card without animation
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: '',
    },

    description: {
      type: String,
      default: '',
    },

    buttonText: {
      type: String,
      default: '',
    },

    passiveText: {
      type: String,
      default: '',
    },

    passiveSecondaryText: {
      type: String,
      default: '',
    },

    gradient: {
      type: String,
      default: 'sduigenta',
    },

    backgroundGradient: {
      type: Boolean,
      default: true,
    },

    showUndoButton: {
      type: Boolean,
      default: true,
    },

    hideCloseButton: {
      type: Boolean,
      default: false,
    },

    hideAction: {
      type: Boolean,
      default: false,
    },

    buttonClasses: {
      type: String,
      default: '',
    },

    buttonCyData: {
      type: String,
      default: '',
    },

    isNavigationalAction: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    cardStatus: {
      flip: false,
      show: true,
      animate: false,
    },

    cardTimeout: null,
  }),

  computed: {
    isIE() {
      return !!(window.document && window.document.documentMode)
    },

    classes() {
      return [
        this.backgroundGradient ? 'with-gradient' : 'border border-gray-100',
        this.gradient && this.backgroundGradient ? `gradient-${this.gradient}` : '',
      ]
    },
  },

  emits: ['action', 'close'],

  methods: {
    handleAction() {
      this.$emit('action')
    },

    hideHintWithAnimation() {
      this.cardStatus.flip = true
      const self = this
      self.$emit('track-close')
      // prevent user from losing focus, when card is flipped
      this.$refs.undoButton.focus()

      // wait for 3 seconds and then hide card
      this.cardTimeout = setTimeout(() => {
        self.cardStatus.animate = true

        // remove card from DOM once slide up animation is completed
        setTimeout(() => {
          self.cardStatus.show = false
          self.$emit('close')
        }, 1000)
      }, 3000)
    },

    async hideWithoutAnimation() {
      // when user closes card, it will not be shown any more
      this.cardStatus.show = false
      this.$emit('close')
    },

    cancelHide() {
      clearTimeout(this.cardTimeout)
      this.cardTimeout = null
      this.cardStatus.animate = false
      this.cardStatus.flip = false
    },
  },

  watch: {
    cardState() {
      // if this prop is changing, parent component asks for hiding card without animation
      this.hideWithoutAnimation()
    },
  },

  beforeUnmount() {
    // We need this to make sure card will not show up again, if user clicks on close and leaves page (so when card has no time to slide out)
    if (!this.cardStatus.flip) return
    this.$emit('close')
  },
}
</script>

<style scoped lang="scss">
.scene {
  perspective: 600px;
  overflow: hidden;
  transition: 0s;

  &.animate {
    max-height: 0;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    transition: 0.8s;
  }

  .hint-inner {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 16px;
    margin-bottom: 0 !important;

    .side {
      width: 100%;
      height: 100%;
      backface-visibility: hidden;
      border-radius: 16px;
      transition: transform 0.5s;

      &.back-side {
        background-color: $color-secondary;
        position: absolute;
        left: 0;
        top: 0;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 10%;
        transform: perspective(800px) rotateX(180deg);
      }

      &.front-side {
        background-color: $color-secondary;
        border-color: $color-gray-100;
        transform: perspective(800px) rotateY(0deg);
      }

      &.with-gradient {
        color: $color-white;

        .buttons-wrapper button {
          background-color: $color-secondary;

          &:hover {
            background-color: $color-secondary;
          }
        }

        *:focus-visible {
          outline-color: $color-white !important;
        }
      }
    }

    &.no-transition {
      .side {
        transition: 0s; // Removing buggy transition from IE
      }
    }

    &.flipped {
      .side {
        &.back-side {
          transform: perspective(800px) rotateX(0);
        }

        &.front-side {
          transform: perspective(800px) rotateX(-179.9deg);
        }
      }

      .card::before {
        opacity: 0;
      }
    }

    :deep(.card-body) {
      padding: 0 !important;
      min-height: 120px;
    }

    .description {
      opacity: 0.8;
      white-space: pre-line;
    }

    .title-wrapper {
      display: flex;
      justify-content: space-between;

      p {
        max-width: calc(100% - 30px);
      }

      i {
        font-size: pxtorem(24);
      }
    }

    @media (max-width: 320px) {
      font-size: pxtorem(14);
      > * {
        font-size: pxtorem(14);
      }
    }
  }

  .btn-shadow {
    box-shadow: 0 4px 12px 0 rgba($color-black-rgb, 0.12);
  }
}

.reset-button {
  color: inherit !important;
  // display: unset is needed for the school-year-change-btn, where buttons aren't displayed.
  display: unset !important;
}

:deep(.hint-button) {
  margin-top: auto;
  margin-left: auto;
}
</style>
